import React, { FunctionComponent, useMemo, useState, useEffect } from "react";
import { bookingService } from "../../../services/BookingService";
import { tableUtils } from "../../../utils/tableUtils";
import { BOOKING_MANAGEMENT_TABLE } from "../../../constants/Table";
import PagedTable from "../table/PagedTable";
import { useIntl } from "react-intl";
import { BookingManagementTotalPriceDto, BookingSearchObject } from "interfaces/BookingInterfaces";
import FieldLabel from "components/atoms/FieldLabel";
import { moneyUtils } from "utils/moneyUtils";
import FilterGroup from "../filters/FilterGroup";
import DateFilter from "../filters/DateFilter";
import { ColorType } from "types/bootstrap/BootstrapType";
import Button from "../../atoms/Button";
import AutocompleteFilterPageable from "../filters/AutocompleteFilterPageable";
import { customerService } from "services/CustomerService";
import { supplierService } from "services/SupplierService";
import { toastUtils } from "utils/toastUtils";
import { FileData } from "interfaces/ResourceInterfaces";
import { fileUtils } from "utils/fileUtils";
import SelectFilter from "../filters/SelectFilter";
import { YES_NO_OPTIONS } from "constants/OptionConstants";

interface BookingManagementListProps {
  className?: string;
  searchObject?: BookingSearchObject;
  updateSearchField?: (field: string, value: string) => void
  onReset?: () => void,
}

const BookingManagementList: FunctionComponent<BookingManagementListProps> = ({
  className = "",
  searchObject,
  updateSearchField,
  onReset
}) => {
  const intl = useIntl();

  const [totalPriceByGroup, setTotalPriceByGroup] = useState<BookingManagementTotalPriceDto | null>(null);
    const [loading, setLoading] = React.useState<boolean>(false)

  const fetchTotalPriceByGroup = (searchParams: BookingSearchObject) => {
    bookingService.getTotalPrice(searchParams)
      .then(setTotalPriceByGroup)
      .catch(() => toastUtils.errorToast(intl.formatMessage({id: "error_toast_get_total_price"})));
  };

  useEffect(() => {
    fetchTotalPriceByGroup(searchObject || {});
  }, [searchObject]);

  const columns = useMemo(
    () => tableUtils.getColumns(BOOKING_MANAGEMENT_TABLE(intl)),
    [intl]
  );

  const downloadExport = (searchObject: BookingSearchObject) => {
    if (!searchObject.startDate) return
    setLoading(true)
    const groupType = searchObject.groupType ? `-${searchObject.groupType}` : "";
    const fileData: FileData = {name: `export-bookings-summary${groupType}`, extension: "csv"}
    fileUtils.downloadFile(
      bookingService.getBookingManagementSummaryCsv(searchObject),
      fileData,
      intl,
      () => setLoading(false)
    )
  }

  return (
    <div className="pt-2">
      <FilterGroup className="d-flex gap-2 mb-3">
        <AutocompleteFilterPageable
          onChange={(value) => updateSearchField("supplierId", value)}
          title={intl.formatMessage({id: "SUPPLIER"})}
          value={searchObject?.supplierId}
          fetchData={supplierService.getSuppliersPaginated}
          filterFieldName="name"
        />
        <AutocompleteFilterPageable
          onChange={(value) => updateSearchField("customerId", value)}
          title={intl.formatMessage({id: "order_filter_customer"})}
          value={searchObject?.customerId}
          fetchData={customerService.getCustomersPage}
          filterFieldName="name"
        />
        <SelectFilter
          options={YES_NO_OPTIONS.map(o => ({label: intl.formatMessage({id: o.label}), value: o.value}))}
          onChange={(value) => updateSearchField("isDisbursed", value)}
          title={intl.formatMessage({id: "customer_disbursement_field"})}
          value={searchObject?.isDisbursed != null ? String(searchObject?.isDisbursed): undefined} 
          isClearable
        />
        <DateFilter
          title={intl.formatMessage({id: "filter_from"})}
          value={searchObject.startDate}
          onChangeDate={(value) => updateSearchField("startDate", value)}
        />
        <DateFilter
          title={intl.formatMessage({id: "filter_to"})}
          value={searchObject.endDate}
          onChangeDate={(value) => updateSearchField("endDate", value)}
        />
        <Button onClick={() => onReset()} color={ColorType.SECONDARY} >{intl.formatMessage({id: "header_menu_clear_search"})}</Button>
        <div className="ms-auto">
          <Button onClick={() => downloadExport(searchObject)} color={ColorType.SECONDARY} >
            {loading && <span className="loader loader-button-group ms-1"/>}
            {intl.formatMessage({id: "export_csv_button"})}
          </Button>
        </div>
      </FilterGroup>
      <div className="mb-2 d-flex">
        <div className="totals-card">
          <FieldLabel
            label={intl.formatMessage({id: "booking_management_total_period"})}
            value={moneyUtils.formatNumberToCurrency(totalPriceByGroup?.totalPriceHTByGroup)}
          />
          <FieldLabel
            label={intl.formatMessage({id: "booking_management_total_disbursement"})}
            value={moneyUtils.formatNumberToCurrency(totalPriceByGroup?.totalDisbursementHTByGroup)}
          />
          <FieldLabel
            label={intl.formatMessage({id: "booking_management_total_billed_disbursement"})}
            value={moneyUtils.formatNumberToCurrency(totalPriceByGroup?.totalDisbursementBilledHTByGroup)}
          />
        </div>
      </div>
      <PagedTable
        columns={columns}
        labelNoResult="booking_management_no_result"
        className={`${className} table`}
        fetchData={bookingService.getBookingsByGroupType}
        filters={searchObject}
        defaultSortBy={[{sortBy: "date"}]}
      />
    </div>
  );
};

export default BookingManagementList;
